// `gifts/product-rules/public` is under the menu tag, not marketing: the generated tags follow the
// backend's controllers rather than this app's modules.
import { CategoryIcon, cn } from '@agos/ui'
import {
  getProductsProductIdAddons,
  useGetBurgerBoxConfig,
  useGetCategories,
  useGetGiftsProductRulesPublic,
  useGetPizzaIngredients,
  useGetProducts,
} from '@agos/api-client/menu'
import { useGetHomepageModules } from '@agos/api-client/marketing'
import { useGetCentralBranding } from '@agos/api-client/platform'
import {
  useGetSettingsGeneral,
  useGetSettingsLunchPublic,
  useGetSettingsOpeningHours,
  useGetSettingsPizzaConfig,
} from '@agos/api-client/platform'
import {
  composedLine,
  composedSubtotalCents,
  lineKey,
  lunchDateKey,
  lunchMealsIn,
  lunchServingNow,
  lunchSubtotalCents,
  mayOrder,
  announcementOf,
  announcementShows,
  bannerNote,
  openState,
  priceLines,
  reconcile,
  subtotalCents,
  totalItems,
  useCart,
  useMinute,
  type OpeningHours,
  type PricedProduct,
} from '@agos/customer-core'
import {
  T,
  formatCents,
  gramazFor,
  pizzaSections,
  type SizedProduct,
  sizeWeightText,
  productSizes,
  offersSizeChoice,
  isKomboEnabled,
  komboSizeCm,
  sizeCmFromName,
  boxFixedPricesOf,
  normalisePizzaConfig,
  tt,
  type BuilderIngredient,
  type KomboPizza,
} from '@agos/shared'
import { Clock, Info, Minus, Pizza, Plus, ShoppingBag, Soup } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'wouter'

import { HomeModuleSection } from './HomeModules'

import { AddonPrompt } from './AddonPrompt'
import { PizzaExtrasPrompt } from './PizzaExtrasPrompt'
import { addonsOf, type Addon } from './addons'
import { BoxBuilder } from './BoxBuilder'
import { boxConfigsOf, boxesFor, boxGroupId, boxLines, type BoxSelection } from './burger-box'
import { giftLine, giftOffers, giftRulesOf, type CountableLine } from '@agos/customer-core'
import { CustomPizzaBuilder } from './CustomPizzaBuilder'
import { KomboBuilder } from './KomboBuilder'
import { LunchMenuModal } from './LunchMenuModal'
import { lunchSettingsOf, lunchWeeksOf, upcomingLunchDays } from './lunch-days'
import { franchiseModulesOf, modulesOf, renderable, unknownNote, withinTier } from './modules'
import { nextOpening, todayOpening } from './opening-banner'
import { serviceExplanation, serviceHeading, serviceStateOf } from './service-state'

/**
 * The menu, and everything the restaurant configured above it.
 *
 * **The category bar decides what this page is.** Legacy's *Úvodná strana* is a pseudo-category: with it
 * selected the page is the franchise's homepage modules and no products at all; picking a real category
 * replaces them with that category's grid. The first AGOS draft rendered the modules *and* every product
 * in one flat list, which is a different page — twice as long as legacy's on a phone, and with no way to
 * reach a category.
 *
 * Two of C0's findings are load-bearing here rather than decorative.
 *
 * **FB-C06 gates every add.** `openState` answers `unknown` when the hours could not be read, and
 * ordering is not offered on it — but the menu still browses, because browsing does not depend on the
 * answer. Legacy assumes open, and nothing behind it checks the hours for an immediate order.
 *
 * **FB-C02 reconciles on load.** The basket holds identity and quantity; the catalogue is priced now,
 * and anything withdrawn since is named and dropped rather than silently charged for.
 */
/**
 * How long the button says *Pridané!* — §9.66.
 *
 * Long enough to read at a glance, short enough that it is gone before somebody decides on the next
 * item. It is a confirmation, not a state.
 */
const ADDED_CONFIRMATION_MS = 1_400

export function HomePage() {
  const { data: moduleData } = useGetHomepageModules()
  // §14.1: legacy refetches this every 60 seconds, and the interval is copied rather than judged — a
  // restaurant that closes early, or flips `emergencyClosed`, must reach a page somebody is already
  // looking at. `useMinute` below re-renders; this is what makes the answer new.
  const {
    data: hoursData,
    isLoading: hoursLoading,
    isError: hoursError,
  } = useGetSettingsOpeningHours({ query: { refetchInterval: 60_000 } })
  const { data: productData } = useGetProducts({})
  const { data: categoryData } = useGetCategories()
  const { data: brandingData } = useGetCentralBranding(undefined, {
    query: { staleTime: 5 * 60_000 },
  })
  // §10A.48 — only the weeks a customer can order from. The whole section is 21 weeks and 230 KB on
  // every page load, and `upcomingLunchDays` looks at most 21 days ahead, so the rest is history.
  // Absent would answer all of them, exactly as before the parameter existed.
  const { data: lunchData } = useGetSettingsLunchPublic({ from: lunchDateKey(new Date()) })
  const { data: pizzaConfig } = useGetSettingsPizzaConfig()
  const { data: pizzaIngredientData } = useGetPizzaIngredients()
  const { data: boxData } = useGetBurgerBoxConfig()
  const { data: giftData } = useGetGiftsProductRulesPublic()
  const { data: generalData } = useGetSettingsGeneral()

  const lines = useCart((state) => state.lines)
  const add = useCart((state) => state.add)
  /** The product somebody just tried to order that the kitchen has switched off — §9.155. */
  const [unavailable, setUnavailable] = useState<string | null>(null)
  const changeBy = useCart((state) => state.changeBy)
  const keepOnly = useCart((state) => state.keepOnly)

  // The open banner and the lunch chip are both read off the clock. Without this they are read once and
  // stay wrong until the customer navigates — 09:44 says closed at 09:46. The C0 spec names the lunch
  // half of it: *a lunch window that opens while somebody is looking at the page should open on the
  // page*.
  useMinute()

  const [category, setCategory] = useState<number | null>(null)
  const [lunchOpen, setLunchOpen] = useState(false)
  const [komboOpen, setKomboOpen] = useState(false)
  const [dismissedGifts, setDismissedGifts] = useState<ReadonlySet<number>>(new Set())
  const [addonPrompt, setAddonPrompt] = useState<{ name: string; addons: Addon[] } | null>(null)
  // §9.168 — the toppings question, which is asked *before* the add because it changes the line.
  const [extrasPrompt, setExtrasPrompt] = useState<{
    product: PricedProduct
    size?: string
    sizeCm: number
  } | null>(null)
  // §9.66 — which button is currently saying *Pridané!*, and the timer that clears it.
  const [justAdded, setJustAdded] = useState<number | null>(null)
  const addedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      // Cleared on unmount: a timer that fires into a gone component is a warning in the console and
      // a leak in a page somebody navigated away from.
      if (addedTimer.current !== null) clearTimeout(addedTimer.current)
    },
    [],
  )

  // The franchise's package decides which sections it may show, and an absent answer shows them all.
  const modules = useMemo(
    () => withinTier(modulesOf(moduleData), franchiseModulesOf(brandingData)),
    [moduleData, brandingData],
  )
  const products = useMemo(
    () => (Array.isArray(productData) ? (productData as PricedProduct[]) : []),
    [productData],
  )
  const categories = useMemo(
    () =>
      (Array.isArray(categoryData)
        ? (categoryData as {
            id?: number
            name?: string
            /** §9.72 — the emoji beside the name, where the franchise set one. */
            icon?: string | null
            visibleOnWeb?: boolean
            hideImage?: boolean
          }[])
        : []
      ).filter((entry) => entry.visibleOnWeb !== false),
    [categoryData],
  )

  // Unknown while the answer is still coming, and unknown if it never does — never open.
  const hours = hoursLoading || hoursError ? null : (hoursData as OpeningHours | null)
  const now = new Date()
  const state = openState(hours, now)
  // §9.243 — a field published since the port that `openingHoursOf` dropped on the floor.
  const announcement = announcementOf(hoursData)
  const ordering = mayOrder(state)

  // FB-C02: the basket is checked against the catalogue rather than trusted — and **not pruned behind
  // the customer's back**. A line whose product has been withdrawn is shown as unavailable with a way to
  // remove it, because a basket that quietly shrinks looks like a bug in the site rather than a change
  // on the menu. Derived, not stored: pruning in an effect would also be the cascading-setState shape
  // the shell's lint rule refuses, and it caught this one too.
  const withdrawn = useMemo(
    () => (products.length === 0 ? [] : reconcile(lines, products).missing),
    [lines, products],
  )

  // The daily menu, which is its own catalogue: a chip on the bar, and a sheet behind it.
  const lunchSettings = useMemo(() => lunchSettingsOf(lunchData), [lunchData])
  const lunchDays = useMemo(
    () => upcomingLunchDays(lunchWeeksOf(lunchData), now),
    // `now` is a fresh Date each render; the days only change when the menu does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lunchData],
  )
  const todayLunchCount = lunchDays[0]?.meals.length ?? 0
  // §9.17 made the window a fixed five days, so `lunchDays.length` is now always five and says
  // nothing about whether a menu exists. The section appears when some day in the window actually has
  // meals — otherwise a franchise that authored nothing would advertise an empty daily menu.
  const showLunch = lunchSettings.visible && lunchDays.some((day) => day.meals.length > 0)
  const serving = lunchServingNow(lunchSettings.timeFrom, lunchSettings.timeTo, now)
  // Lunch lines are priced by their own rule rather than from the catalogue, so they are totalled
  // separately and added to the subtotal the strip shows. The one day a basket may hold is the one
  // whose meals resolve.
  // §9.399 - **one entry per basket line, not per day.** This mapped every window day over the
  // lines, and `lunchMealToCartId` hashes the meal - so once §10A.176 made one authored week serve
  // every week, the same Friday soup appeared on two days of the window and the strip charged it
  // twice: 16,80 € against the 8,40 € the sheet had just shown. `lunchMealsIn` walks the **lines**
  // and pushes at most one entry each, so flattening the window's meals into one lookup is both
  // simpler and the fix.
  const lunchBasket = useMemo(
    () => lunchMealsIn(lines, lunchDays.flatMap((day) => day.meals)),
    [lunchDays, lines],
  )
  const lunchInBasket = lunchBasket.reduce((count, entry) => count + entry.quantity, 0)
  const lunchCents = lunchSubtotalCents(lunchBasket, lunchSettings.soupBundlePrice ?? null)
  const composedCents = composedSubtotalCents(lines)

  // The kombo builder belongs to the pizza category and nowhere else: it is three pizzas in one box,
  // and offering it under Nápoje would be a control with nothing to fill it.
  const activeCategoryName = categories.find((entry) => entry.id === category)?.name ?? ''
  const isPizzaCategory = activeCategoryName.toLowerCase().includes('pizza')
  const komboCm = komboSizeCm(normalisePizzaConfig(pizzaConfig))
  // §9.19 — the custom-pizza builder, offered where the kombo is: the pizza category and nowhere else.
  const [customPizzaOpen, setCustomPizzaOpen] = useState(false)
  // The wire field is `active`. This read `isActive`, which is on no row the endpoint answers, so
  // `undefined !== false` held for all 38 and the guard had never removed anything — §9.25. Both
  // spellings now, here and in `ingredientGroups`, so neither surface loses one.
  const pizzaIngredients = useMemo(
    () =>
      (Array.isArray(pizzaIngredientData) ? pizzaIngredientData : []).filter((ingredient) => {
        const row = ingredient as { active?: boolean; isActive?: boolean }
        return row.active !== false && row.isActive !== false
      }) as unknown as BuilderIngredient[],
    [pizzaIngredientData],
  )
  const showKombo = isPizzaCategory && isKomboEnabled(normalisePizzaConfig(pizzaConfig))
  // §9.244 — how many pizza builders this category actually offers, so the grid can be that wide.
  const builderCount =
    (isPizzaCategory && pizzaIngredients.length > 0 ? 1 : 0) + (showKombo ? 1 : 0)
  // The pizzas a kombo may be built from: the ones on the menu in this category, priced.
  const pizzasForKombo = useMemo(
    () =>
      products.filter(
        (product) =>
          product.active !== false &&
          (product as { visibleOnWeb?: boolean }).visibleOnWeb !== false &&
          (product as { categoryId?: number }).categoryId === category,
      ) as unknown as KomboPizza[],
    [products, category],
  )

  // The burger box, offered by the category its config names — `box_legacy` is active on this data.
  // §9.200 — every box this category offers, not the first. `boxFor` ended in `.find`, so a
  // second configured box was parsed, held and never drawn.
  const boxConfigs = useMemo(() => boxConfigsOf(boxData), [boxData])
  const boxes = useMemo(() => boxesFor(boxConfigs, category), [boxConfigs, category])
  /**
   * §10A.107 - every configured box's fixed price, so the basket shows what the server will bill.
   *
   * Off **every** config rather than only the ones this category offers: a box added from another
   * category is still in the basket, and pricing it as the sum of its parts here while the server
   * charged the fixed price is the mismatch this map exists to prevent.
   */
  const fixedBoxPrices = useMemo(() => boxFixedPricesOf(boxData), [boxData])

  /**
   * What the basket has earned and not taken — measured on this data, one active rule: two pizzas earn
   * a 2 € gift, multiplying.
   *
   * Dismissed offers are held for the session, not forever: a customer who says no to one gift and then
   * adds two more pizzas is offered the next one. Legacy keeps the same list in `sessionStorage`.
   */
  const countable: CountableLine[] = useMemo(
    () =>
      lines.map((line) => ({
        line,
        categoryName:
          categories.find(
            (entry) =>
              entry.id ===
              (
                products.find((product) => product.id === line.productId) as
                  { categoryId?: number } | undefined
              )?.categoryId,
          )?.name ?? '',
      })),
    [lines, products, categories],
  )
  const offers = giftOffers(giftRulesOf(giftData), countable).filter(
    (offer) => !dismissedGifts.has(offer.ruleId),
  )
  const offer = offers[0] ?? null

  const priced = priceLines(lines, products, fixedBoxPrices)
  const hideImage = categories.find((entry) => entry.id === category)?.hideImage === true
  const visible = products.filter(
    (product) =>
      product.active !== false &&
      (product as { visibleOnWeb?: boolean }).visibleOnWeb !== false &&
      category !== null &&
      (product as { categoryId?: number }).categoryId === category,
  )

  /**
   * The category's products, split into the sections legacy shows — FRONTEND.md §9.16.
   *
   * Only the pizza category has sub-categories on this data, and `pizzaSections` answers a single
   * untitled section for everything that carries none — so every other category renders exactly as it
   * did, one grid, no headings.
   */
  const sections = useMemo(
    () => pizzaSections(visible as unknown as Array<{ pizzaSubcategory?: string | null }>),
    [visible],
  )

  /**
   * Add a product, then ask what goes with it.
   *
   * **Add first, ask second** — legacy's order, and the reason is that the question is an offer. The add
   * is never awaited on the fetch: a customer who taps twice must get two of the thing, whatever the
   * add-ons endpoint is doing, and a failed lookup must not swallow the add.
   */
  function addProduct(product: PricedProduct) {
    // §9.155, the owner's point 43 — *Dočasne nedostupné*. Guarded here rather than at each button,
    // which is legacy's own placement and the reason is coverage: cards, size variants and the
    // add-on prompt all arrive through this one function. `!== false` keeps selling anything whose
    // payload predates the field.
    if ((product as { orderable?: boolean }).orderable === false) {
      setUnavailable(product.name)
      return
    }
    // §9.168 — a pizza is asked what goes *on* it before it is added, because the answer changes
    // the line. An add-on is a second product and is asked after, which is why the two prompts sit
    // on opposite sides of this call. Both orders are legacy's.
    if (isPizzaCategory && pizzaIngredients.length > 0) {
      setExtrasPrompt({
        product,
        sizeCm: sizeCmFromName(product.name) ?? komboCm,
      })
      return
    }
    addPlain(product)
  }

  /**
   * A sized product's first add — §9.85 and §9.168 together.
   *
   * The size decides both prices here: the pizza's, from `pizza_sizes`, and the topping's, because
   * an ingredient costs more on a bigger pizza. Sending the wrong one would quote a 50 cm topping at
   * the 32 cm price, which is §10A.33's loss in miniature.
   */
  function addSized(product: PricedProduct, size: string) {
    if ((product as { orderable?: boolean }).orderable === false) {
      setUnavailable(product.name)
      return
    }
    if (isPizzaCategory && pizzaIngredients.length > 0) {
      setExtrasPrompt({ product, size, sizeCm: sizeCmFromName(size) ?? komboCm })
      return
    }
    add({ productId: product.id, extras: '', quantity: 1, size })
  }

  /** The add itself, and the add-on question after it. Legacy's order: add first, ask second. */
  function addPlain(product: PricedProduct) {
    add({ productId: product.id, extras: '', quantity: 1 })
    confirmAdded(product.id)
    void getProductsProductIdAddons(product.id)
      .then((payload) => {
        const addons = addonsOf(payload)
        if (addons.length > 0) setAddonPrompt({ name: product.name, addons })
      })
      .catch(() => undefined)
  }

  /**
   * §9.66 — the button says *Pridané!* for a moment after it is pressed.
   *
   * The fly-to-cart dot is the other half of this and gives up on
   * `prefers-reduced-motion: reduce`, so for a customer who asked for less motion this is the **only**
   * confirmation an add produces. On the button rather than a toast because a menu is a screen people
   * add several things from, and a toast per add stacks over the next item.
   *
   * One id at a time: adding a second item moves the confirmation to the second button, which is
   * where the eye already is. The timer is replaced rather than stacked, so a quick second press does
   * not leave the first button stuck saying it.
   */
  function confirmAdded(productId: number) {
    setJustAdded(productId)
    if (addedTimer.current !== null) clearTimeout(addedTimer.current)
    addedTimer.current = setTimeout(() => setJustAdded(null), ADDED_CONFIRMATION_MS)
  }

  function quantityOf(productId: number, size?: string): number {
    return (
      lines.find(
        (line) => line.productId === productId && line.extras === '' && line.size === size,
      )?.quantity ?? 0
    )
  }

  // Two switches close the shop, and neither is the opening hours: maintenance, and online orders being
  // turned off. Both fail open — a settings call that did not answer must not shut a restaurant that is
  // trading. Checked after the hooks, because a component may not return before them.
  const service = serviceStateOf(generalData)

  const chip = (active: boolean) =>
    `px-2.5 py-1 rounded-full font-extrabold text-xs whitespace-nowrap transition-all duration-200 active:opacity-70 ${
      active ? 'bg-white text-black shadow-md' : 'bg-transparent text-white hover:bg-white/15'
    }`

  if (service !== 'open') {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center bg-background px-4 text-center gap-6"
        data-service-state={service}
      >
        <div className="text-6xl">{service === 'maintenance' ? '🔧' : '🚫'}</div>
        <h1 className="text-2xl font-bold text-foreground">{serviceHeading(service)}</h1>
        <p className="text-muted-foreground max-w-sm">{serviceExplanation(service)}</p>
      </div>
    )
  }

  return (
    <div className="pb-32 text-base min-h-screen overflow-x-hidden bg-white" data-customer-home>
      {/* The bar is black, sticky, and scrolls sideways rather than wrapping - a restaurant with
          eighteen categories would otherwise push the menu below the fold on a phone. */}
      <div
        id="categories-section"
        className="sticky top-0 z-40 bg-black backdrop-blur-md pt-2 pb-3"
      >
        <div className="relative mx-auto w-full">
          <div className="px-3 w-full overflow-x-auto hide-scrollbar">
            <div className="flex flex-nowrap justify-start gap-1 w-max mx-auto" data-categories>
              <button
                type="button"
                data-category="home"
                onClick={() => setCategory(null)}
                aria-pressed={category === null}
                className={chip(category === null)}
              >
                <T>Úvodná strana</T>
              </button>
              {showLunch && (
                <button
                  type="button"
                  data-lunch-menu
                  onClick={() => setLunchOpen(true)}
                  className="px-2.5 py-1 rounded-full font-extrabold text-xs whitespace-nowrap transition-all duration-200 flex items-center gap-1 bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"
                >
                  <Soup className="w-4 h-4" />
                  {/* Before serving starts, and after it stops, the same menu is a preorder - and
                      saying so is the difference between a customer expecting lunch in ten minutes
                      and one expecting it tomorrow. */}
                  {serving ? <T>Obedové menu</T> : <T>Obedové menu (predobjednávka)</T>}
                  <span
                    data-lunch-count
                    className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold bg-amber-500 text-white"
                  >
                    {todayLunchCount}
                  </span>
                </button>
              )}
              {categories.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  data-category={entry.id}
                  onClick={() => setCategory(entry.id ?? null)}
                  aria-pressed={category === entry.id}
                  className={chip(category === entry.id)}
                >
                  {/* §9.72 chose a free emoji over a curated set; **§9.91 reversed that on the
                    * owner's word** — *"remove emojis, keep the one from obedové menu and replace
                    * them with these"*. The column is unchanged and still free text; what draws is
                    * the set's glyph where the value, or failing that the category's own name, names
                    * one. An emoji no name can explain is still drawn as stored. */}
                  {entry.icon !== null && entry.icon !== undefined && entry.icon !== '' && (
                    <span data-category-icon={entry.id} className="mr-1 inline-flex align-middle">
                      {/* §9.91 - the daily menu's own glyph, from the stored name or the category's. */}
                      <CategoryIcon icon={entry.icon} name={entry.name} className="w-3.5 h-3.5" />
                    </span>
                  )}
                  {entry.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* FB-C06: unknown says so, and does not offer to take an order under it. Legacy's banner is red
          and says when the doors open again; on `unknown` there is no such answer, and it says that
          instead of guessing one. */}
      {!ordering && (
        <div className="bg-red-600 text-white" data-open-state={state}>
          <div className="max-w-5xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center gap-3 text-center sm:text-left">
            <div className="flex items-center gap-2.5 shrink-0">
              <div className="w-2.5 h-2.5 rounded-full bg-white/70" />
              <span className="font-bold text-lg">{bannerNote(state)}</span>
            </div>
            {todayOpening(hours, now) !== null && (
              <span className="text-white/80 text-sm" data-opens-today>
                <T>Dnes otvárame o</T>{' '}
                <strong className="text-white">{todayOpening(hours, now)}</strong> <T>hod.</T>
              </span>
            )}
            {todayOpening(hours, now) === null && nextOpening(hours, now) !== null && (
              <span className="text-white/80 text-sm" data-opens-next>
                <T>Nabudúce otvárame</T>{' '}
                <strong className="text-white">{nextOpening(hours, now)?.dayLabel}</strong> <T>o</T>{' '}
                <strong className="text-white">{nextOpening(hours, now)?.from}</strong> <T>hod.</T>
              </span>
            )}
            <div className="flex items-center gap-1.5 ml-auto shrink-0 bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
              <Clock className="w-3.5 h-3.5" />
              <T>Objednávky nie sú k dispozícii</T>
            </div>
          </div>
        </div>
      )}

      {/* §9.243 - the owner's own notice, which the manager has always been able to write and no
          customer has ever seen. Below the closed bar, not instead of it: one says the state, the
          other says why. */}
      {announcementShows(announcement, state) && (
        <div className="bg-amber-500/10 border-b border-amber-500/25" data-announcement>
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-start gap-3">
            <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">
              {announcement.message}
            </p>
          </div>
        </div>
      )}

      {withdrawn.length > 0 && (
        <div className="max-w-5xl mx-auto px-4 mt-4">
          <div
            className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-2"
            data-withdrawn
          >
            <p className="text-[11px]">
              <T>
                Niektoré položky z košíka už v ponuke nie sú. Nechávame ich tu, aby ste videli, čo
                sa zmenilo - do sumy sa nepočítajú.
              </T>
            </p>
            <button
              type="button"
              data-drop-withdrawn
              onClick={() => keepOnly(reconcile(lines, products).keep)}
              className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold"
            >
              <T>Odobrať ich</T>
            </button>
          </div>
        </div>
      )}

      {/* Everything the restaurant switched on, in its own order - and only on the home tab. */}
      {category === null && renderable(modules).length > 0 && (
        <div className="max-w-5xl mx-auto px-4 mt-8 space-y-10">
          {renderable(modules).map((module) => (
            <HomeModuleSection
              key={module.key}
              module={module}
              products={products}
              mayOrder={ordering}
              onAdd={(productId) => add({ productId, extras: '', quantity: 1 })}
              onOpenCategory={(categoryId) => setCategory(categoryId)}
            />
          ))}
          {unknownNote(modules) !== '' && (
            <p className="text-[11px] text-amber-500" data-unknown-modules>
              {unknownNote(modules)}
            </p>
          )}
        </div>
      )}

      {/* §9.45 - *"vedľa seba, nie pod sebou, a vlastná pizza má byť prvá"*. They were two full-width
       * blocks with kombo above, which was the opposite of both halves of the request. One row on
       * anything wider than a phone, where side by side would leave each about 150px. */}
      {builderCount > 0 && (
        <div
          // §9.244 — the column count follows what is in the row. Two columns with one tile in them
          // left a hole where the kombo would have been, which is the owner's N10.
          className={`max-w-5xl mx-auto px-4 mt-6 grid gap-3 ${builderCount === 2 ? 'sm:grid-cols-2' : ''}`}
          data-pizza-builders={builderCount}
        >
          {isPizzaCategory && pizzaIngredients.length > 0 && (
            <button
              type="button"
              data-custom-pizza-open
              onClick={() => setCustomPizzaOpen(true)}
              className="w-full flex items-center gap-3 p-4 rounded-3xl border-2 border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors text-left"
            >
              <span className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                <Pizza className="w-5 h-5 text-primary" />
              </span>
              <span className="flex-1">
                <span className="block font-bold text-foreground">
                  <T>Vlastná pizza</T>
                </span>
                <span className="block text-xs text-muted-foreground">
                  <T>Zložte si ju podľa seba</T>
                </span>
              </span>
            </button>
          )}
          {showKombo && (
            <button
              type="button"
              data-kombo-open
              onClick={() => setKomboOpen(true)}
              className="w-full flex items-center gap-3 p-4 rounded-3xl border-2 border-dashed border-orange-300 bg-orange-50 hover:bg-orange-100 transition-colors text-left"
            >
              <span className="w-11 h-11 rounded-2xl bg-orange-100 flex items-center justify-center shrink-0">
                <Pizza className="w-5 h-5 text-orange-500" />
              </span>
              <span className="flex-1">
                <span className="block font-bold text-foreground">
                  <T>Kombo pizza</T>
                </span>
                <span className="block text-xs text-muted-foreground">
                  <T>Tri druhy na jednej</T> · {komboCm} cm
                </span>
              </span>
            </button>
          )}
        </div>
      )}

      {/* §9.87 - a panel, not a dialog: legacy shows the box whenever the customer is in its
        * category, with no button and nothing to open. */}
      {boxes.map((box) => (
        <BoxBuilder
          key={box.id}
          config={box}
          products={products}
          // The box went in; clearing the panel's own selection is BoxBuilder's job.
          onAdded={() => undefined}
          onAdd={(selection: BoxSelection) => {
            // Its own group: `boxGroupId` hashes the config with the chosen slots, so two boxes from
            // one category are two groups and `removeBox` takes the right one.
            const group = boxGroupId(box, selection)
            for (const line of boxLines(box, selection)) {
              add({
                productId: line.productId,
                extras: '',
                quantity: 1,
                boxGroupId: group,
                boxGroupName: box.name,
                boxSlotLabel: line.slotLabel,
              })
            }
          }}
        />
      ))}

      {/* §9.155 - legacy answers a refused add with a message, and so does this. The card is

        * `pointer-events-none`, so this fires for the entry points that are not a card click:

        * a size variant, the add-on prompt, or a stale menu in a tab left open since this morning. */}

      {unavailable !== null && (

        <div

          data-unavailable-notice

          role="status"

          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-sm px-4 py-3 rounded-xl bg-zinc-900 text-white shadow-lg"

        >

          <p className="text-sm font-bold">

            <T>Momentálne nedostupné</T>

          </p>

          <p className="text-xs font-semibold mt-0.5">{unavailable}</p>
          {/* Legacy's sentence, byte for byte - `slovak-parity.test.ts` caught this reworded to a
            * lower-case *t* when the product name was folded into the same line, which is exactly the
            * drift that gate exists to stop. The name goes above it instead. */}
          <p className="text-xs opacity-80">
            <T>Tento produkt je dočasne vypredaný.</T>
          </p>

          <button

            type="button"

            data-unavailable-dismiss

            onClick={() => setUnavailable(null)}

            className="mt-1.5 text-xs underline opacity-80"

          >

            <T>Zavrieť</T>

          </button>

        </div>

      )}


      {/* §9.168 - before the add, unlike the add-on prompt below it. */}
      {extrasPrompt !== null && (
        <PizzaExtrasPrompt
          productName={extrasPrompt.product.name}
          sizeCm={extrasPrompt.sizeCm}
          ingredients={pizzaIngredients}
          onSkip={() => {
            const pending = extrasPrompt
            setExtrasPrompt(null)
            if (pending.size === undefined) {
              addPlain(pending.product)
              return
            }
            // A sized line skips the add-on question too, exactly as it did before §9.168: the size
            // rows have never asked it.
            add({ productId: pending.product.id, extras: '', quantity: 1, size: pending.size })
            confirmAdded(pending.product.id)
          }}
          onConfirm={(extras, surchargeCents, chosen) => {
            const pending = extrasPrompt
            setExtrasPrompt(null)
            add({
              productId: pending.product.id,
              extras,
              quantity: 1,
              ...(pending.size === undefined ? {} : { size: pending.size }),
              ...(chosen.length > 0
                ? { extrasChosen: chosen, extrasCents: surchargeCents }
                : {}),
            })
            confirmAdded(pending.product.id)
          }}
        />
      )}
      {addonPrompt !== null && (
        <AddonPrompt
          productName={addonPrompt.name}
          addons={addonPrompt.addons}
          onClose={() => setAddonPrompt(null)}
          onConfirm={(chosen) => {
            for (const id of chosen) add({ productId: id, extras: '', quantity: 1 })
            setAddonPrompt(null)
          }}
        />
      )}

      {customPizzaOpen && (
        <CustomPizzaBuilder
          config={normalisePizzaConfig(pizzaConfig)}
          ingredients={pizzaIngredients}
          onClose={() => setCustomPizzaOpen(false)}
          onAdd={(line) => add(composedLine(line.name, line.unitPriceCents, line.parts))}
        />
      )}

      {komboOpen && (
        <KomboBuilder
          pizzas={pizzasForKombo}
          komboCm={komboCm}
          onClose={() => setKomboOpen(false)}
          onAdd={(line) =>
            // §10A.26 — `kombo` so the server bounds this at its dearest pizza rather than the sum
            // of the three, which is far too loose a cap for a line priced `max` by design.
            add(
              composedLine(
                `${tt('Kombo pizza')}: ${line.name}`,
                line.unitPriceCents,
                line.parts,
                line.composition,
              ),
            )
          }
        />
      )}

      {category !== null && (
        <div className="max-w-5xl mx-auto px-4" data-menu>
          {sections.map((section) => (
            <div key={section.title ?? '_ungrouped'} data-menu-section={section.title ?? ''}>
              {section.title !== null && (
                <h3
                  className="text-lg font-bold text-foreground mt-6 mb-4 pb-2 border-b border-border"
                  data-pizza-section
                >
                  <T>{section.title}</T>
                </h3>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                {(section.products as unknown as typeof visible).map((product) => {
                  const quantity = quantityOf(product.id)
                  // §9.85 — 30 products are sold in two sizes, each with its own price and weight,
                  // and the card drew only the base. Legacy gives each size its own row.
                  const sizes = productSizes(product as unknown as SizedProduct)
                  const sized = offersSizeChoice(sizes)
                  const unitCents =
                    priceLines([{ productId: product.id, extras: '', quantity: 1 }], products)[0]
                      ?.unitCents ?? 0
                  const imageUrl = (product as { imageUrl?: string | null }).imageUrl ?? null
                  const description =
                    (product as { description?: string | null }).description ?? null
                  const allergens = (product as { allergens?: string | null }).allergens ?? null
                  // §9.155 — absent reads as orderable, so a payload that predates the field sells.
                  const notOrderable = (product as { orderable?: boolean }).orderable === false

                  return (
                    <div
                      key={product.id}
                      data-product={product.id}
                      data-orderable={notOrderable ? 'no' : 'yes'}
                      className={cn(
                        'group bg-card rounded-3xl border border-border shadow-sm hover:shadow-xl hover:border-green-200 transition-all duration-300 flex flex-col relative overflow-hidden',
                        // §9.155 — legacy's own classes. Still there, still readable, and inert.
                        notOrderable && 'opacity-50 grayscale pointer-events-none select-none',
                      )}
                    >
                      {notOrderable && (
                        <div
                          data-unavailable-badge
                          className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full bg-zinc-900/85 text-white text-[11px] font-bold uppercase tracking-wide"
                        >
                          <T>Nedostupné</T>
                        </div>
                      )}
                      {imageUrl !== null && imageUrl !== '' && !hideImage && (
                        <div className="w-full h-40 overflow-hidden bg-muted shrink-0">
                          <img
                            src={imageUrl}
                            alt={product.name}
                            className="w-full h-full object-cover md:group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}
                      <div className="p-5 flex-1 flex flex-col bg-card">
                        <h3 className="font-display text-lg font-bold leading-tight text-foreground mb-1">
                          {product.name}
                          {/* §9.63 - Množstvo + Jednotka, and only where the name does not already
                            * say it. 32 of the 113 products that carry an amount spell it out
                            * themselves, and drawing both would print it twice on nearly a third of
                            * the menu. */}
                          {gramazFor(product) !== null && (
                            <span
                              className="ml-2 text-xs font-bold text-muted-foreground align-middle"
                              data-product-gramaz
                            >
                              {gramazFor(product)}
                            </span>
                          )}
                        </h3>
                        {description !== null && description !== '' && (
                          <p className="text-sm text-muted-foreground mb-2 leading-relaxed">
                            {description}
                          </p>
                        )}
                        {allergens !== null && allergens !== '' && (
                          <p className="text-xs text-muted-foreground mb-2">
                            <T>Alergény</T>: {allergens}
                          </p>
                        )}
                        <div className="mt-auto pt-3 border-t border-border space-y-2">
                          {/* §9.69 - the franchise's own colour, adjusted until it is readable as
                            * ink. `--brand-ink` falls back to the plain foreground for a franchise
                            * that has set no colour, which is this one — so nothing changes until
                            * somebody picks one. */}
                          {/* §9.85 - a row per size, which is how legacy sells these: the label, the
                            * weight for *that* size, its own price and its own control. The card
                            * below is unchanged for the products sold at one price, which is most. */}
                          {sized ? (
                            <div className="space-y-1.5" data-product-sizes={product.id}>
                              {sizes.map((size) => {
                                const sizeQty = quantityOf(product.id, size.label)
                                const weight = sizeWeightText(size)
                                return (
                                  <div
                                    key={size.label}
                                    data-product-size={size.label}
                                    className="flex items-center justify-between gap-2 py-1.5 px-3 rounded-xl bg-muted/40 border border-border"
                                  >
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="text-xs font-black bg-border px-2 py-0.5 rounded-full shrink-0">
                                        {size.label}
                                      </span>
                                      {weight !== null && (
                                        <span
                                          className="text-xs text-muted-foreground shrink-0"
                                          data-size-weight
                                        >
                                          {weight}
                                        </span>
                                      )}
                                      <span className="text-base font-black" data-size-price>
                                        {formatCents(size.priceCents)}
                                      </span>
                                    </div>
                                    {sizeQty === 0 ? (
                                      <button
                                        type="button"
                                        data-add-size={size.label}
                                        aria-label={`${tt('Pridať')} ${product.name} ${size.label}`}
                                        // §9.168 — the *first* add of a size asks what goes on it,
                                        // and the size is what prices a topping. The stepper below
                                        // does not ask: it adds another of a line that exists.
                                        onClick={() => addSized(product, size.label)}
                                        className="shrink-0 w-11 h-11 rounded-full bg-green-600 text-white flex items-center justify-center hover:bg-green-700 active:scale-95 transition-colors"
                                      >
                                        <Plus className="w-4 h-4" />
                                      </button>
                                    ) : (
                                      <div className="shrink-0 flex items-center gap-1">
                                        <button
                                          type="button"
                                          data-decrease-size={size.label}
                                          aria-label={tt('Menej')}
                                          onClick={() =>
                                            changeBy(
                                              lineKey({
                                                productId: product.id,
                                                extras: '',
                                                size: size.label,
                                              }),
                                              -1,
                                            )
                                          }
                                          className="w-11 h-11 rounded-full bg-green-600 text-white flex items-center justify-center hover:bg-green-700 transition-colors"
                                        >
                                          <Minus className="w-4 h-4" />
                                        </button>
                                        <span className="w-6 text-center font-bold">{sizeQty}</span>
                                        <button
                                          type="button"
                                          data-add-size={size.label}
                                          aria-label={tt('Viac')}
                                          onClick={() =>
                                            add({
                                              productId: product.id,
                                              extras: '',
                                              quantity: 1,
                                              size: size.label,
                                            })
                                          }
                                          className="w-11 h-11 rounded-full bg-green-600 text-white flex items-center justify-center hover:bg-green-700 transition-colors"
                                        >
                                          <Plus className="w-4 h-4" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                          <span
                            className="text-2xl font-black"
                            style={{ color: 'var(--brand-ink, hsl(var(--foreground)))' }}
                            data-product-price
                          >
                            {formatCents(unitCents)}
                          </span>
                          )}
                          {/* §9.66 - above the control rather than inside it. The add button only
                            * exists while the quantity is zero, so a confirmation rendered *in* it is
                            * replaced by the stepper the instant it succeeds and is never seen. This
                            * sits beside both. */}
                          {justAdded === product.id && (
                            <p
                              className="text-center text-xs font-bold text-green-700"
                              data-added={product.id}
                            >
                              <T>Pridané do košíka!</T>
                            </p>
                          )}
                          {/* §9.85 - a sized product is added by its own size rows above; one
                            * control for a product with two prices could only ever add the wrong
                            * one. */}
                          {sized ? null : quantity === 0 ? (
                            <button
                              type="button"
                              data-add={product.id}
                              disabled={!ordering}
                              onClick={() => addProduct(product)}
                              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-bold text-sm shadow-md active:scale-95 transition-all duration-200 disabled:opacity-50"
                            >
                              <Plus className="w-5 h-5 stroke-[3]" />
                              <T>Do košíka</T>
                            </button>
                          ) : (
                            <div className="flex items-center justify-center bg-green-50 rounded-2xl p-1 border border-green-200">
                              <button
                                type="button"
                                data-decrease={product.id}
                                aria-label={tt('Menej')}
                                onClick={() =>
                                  changeBy(lineKey({ productId: product.id, extras: '' }), -1)
                                }
                                className="w-11 h-11 rounded-xl bg-green-600 text-white flex items-center justify-center shadow-sm hover:bg-green-700 active:scale-95 transition-colors"
                              >
                                <Minus className="w-5 h-5" />
                              </button>
                              <span className="flex-1 text-center font-bold text-foreground text-lg">
                                {quantity}
                              </span>
                              <button
                                type="button"
                                data-add={product.id}
                                aria-label={tt('Viac')}
                                onClick={() =>
                                  add({ productId: product.id, extras: '', quantity: 1 })
                                }
                                className="w-11 h-11 rounded-xl bg-green-600 text-white flex items-center justify-center shadow-sm hover:bg-green-700 active:scale-95 transition-colors"
                              >
                                <Plus className="w-5 h-5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
          {visible.length === 0 && (
            <p className="text-sm text-muted-foreground col-span-full" data-empty-category>
              <T>V tejto kategórii zatiaľ nič nie je.</T>
            </p>
          )}
        </div>
      )}

      {offer !== null && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-5 bg-black/50 backdrop-blur-sm"
          data-gift-offer={offer.ruleId}
          role="presentation"
          onClick={() => setDismissedGifts(new Set([...dismissedGifts, offer.ruleId]))}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={tt('Darček k objednávke')}
            onClick={(event) => event.stopPropagation()}
            className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            <div className="bg-red-600 px-6 pt-7 pb-5 text-white text-center">
              <div className="text-5xl mb-3">🎁</div>
              <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1">
                <T>Darček k objednávke</T>
              </p>
              <h2 className="text-xl font-extrabold leading-snug">
                {offer.count > 1 ? `${offer.count}× ` : ''}
                {offer.giftName}
              </h2>
              {/* §10A.109 - ***za***, not *v hodnote*. Legacy's home page says *"v hodnote 2,00 €"*
                * and then charges 2,00 €; its own checkout says *"– 2,00 €"*, which is the wording
                * that matches the money. A customer told a gift is *worth* 2 € and then billed 2 €
                * has been misled by one word. */}
              {offer.giftPriceCents > 0 && (
                <p className="text-sm opacity-80 mt-1">
                  <T>za</T> {formatCents(offer.giftPriceCents)}
                </p>
              )}
            </div>
            <div className="px-6 py-5 text-center">
              <p className="text-gray-600 text-sm mb-5">
                <T>Splnili ste podmienky pre darček. Chcete ho pridať do košíka?</T>
              </p>
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  data-gift-accept
                  onClick={() => {
                    add(giftLine(offer))
                  }}
                  className="w-full bg-red-600 hover:bg-red-700 active:scale-95 text-white font-bold py-3.5 rounded-2xl transition-all text-sm"
                >
                  <T>Áno, pridať darček</T>
                </button>
                <button
                  type="button"
                  data-gift-dismiss
                  onClick={() => setDismissedGifts(new Set([...dismissedGifts, offer.ruleId]))}
                  className="w-full text-muted-foreground font-semibold py-2 text-sm"
                >
                  <T>Teraz nie</T>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {lunchOpen && (
        <LunchMenuModal
          days={lunchDays}
          settings={lunchSettings}
          now={now}
          serving={serving}
          categories={categories}
          products={products}
          onClose={() => setLunchOpen(false)}
        />
      )}

      {/* §9.165 - legacy's pill. What the owner was pointing at is the phrase *N položiek*, which is
          now the green badge on the bag: a count is a number, and spelling it out took a row.
          `text-background`, never `text-white` - legacy's own comment on this bar says so and it is
          right. The pill is `bg-foreground`; the pair swaps between the light and dark schemes, so a
          hardcoded white vanishes against the dark scheme's near-white bar. */}
      {lines.length > 0 && (
        <aside
          className="fixed bottom-16 md:bottom-6 left-0 right-0 z-30 px-4 flex justify-center pointer-events-none"
          data-cart-strip
          aria-label={tt('Košík')}
        >
          {ordering ? (
            <Link
              href="/checkout"
              data-to-checkout
              className="pointer-events-auto w-full max-w-md bg-foreground text-background shadow-2xl rounded-2xl p-4 flex items-center justify-between gap-3 hover:scale-[1.02] active:scale-95 transition-all duration-300"
            >
              <BasketSummary
                count={totalItems(lines)}
                totalCents={subtotalCents(priced) + lunchCents + composedCents}
                lunchCents={lunchInBasket > 0 ? lunchCents : null}
              />
              <span className="bg-green-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm shrink-0">
                <T>Objednať</T>
              </span>
            </Link>
          ) : (
            // Legacy has no closed state on this bar, so there is nothing to copy. A pill that looks
            // pressable and is not is worse than the card it replaces, so this one is a `div`.
            <div className="pointer-events-auto w-full max-w-md bg-foreground text-background shadow-2xl rounded-2xl p-4 flex items-center justify-between gap-3">
              <BasketSummary
                count={totalItems(lines)}
                totalCents={subtotalCents(priced) + lunchCents + composedCents}
                lunchCents={lunchInBasket > 0 ? lunchCents : null}
              />
              <span className="text-[11px] text-background/70 text-right shrink-0 max-w-[9rem]">
                {tt('Objednávku zatiaľ nedáme.')}
              </span>
            </div>
          )}
        </aside>
      )}
    </div>
  )
}

/**
 * The left half of the basket pill — §9.165.
 *
 * The count lives in the badge and nowhere else. It was the phrase *N položiek* that the owner
 * objected to, and `data-strip-count` is what the e2e reads now — `data-cart-count` was taken by the header badge: `customer-home.spec.ts` records that
 * both of its assertions once matched a bare `2` **by luck of this data** — a product priced 2,50 €
 * would have satisfied them with the count wrong — and an element holding nothing but the number is
 * the selector that warning was asking for.
 */
function BasketSummary(props: { count: number; totalCents: number; lunchCents: number | null }) {
  return (
    <span className="flex items-center gap-3 min-w-0">
      <span className="bg-background/20 w-10 h-10 rounded-full flex items-center justify-center relative shrink-0">
        <ShoppingBag className="w-5 h-5 text-background" />
        <span
          data-strip-count
          className="absolute -top-1 -right-1 bg-green-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-foreground"
        >
          {props.count}
        </span>
      </span>
      <span className="flex flex-col min-w-0">
        <span className="text-xs text-background/70 font-medium uppercase tracking-wider">
          <T>Zobraziť košík</T>
        </span>
        <span className="font-bold text-lg text-background">{formatCents(props.totalCents)}</span>
        {props.lunchCents !== null && (
          <span className="text-[11px] text-background/70" data-lunch-in-basket>
            <T>Z toho obedové menu</T>: {formatCents(props.lunchCents)}
          </span>
        )}
      </span>
    </span>
  )
}
